import { pageOffset, type MailStatus, type SentMailSummary } from '@myrewards/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { assistantMailStatusQuery } from '../api/ai';
import { sentMailQuery } from '../api/notifications';
import { Notice } from '../ops/shared';
import { AdminFrame } from './frame';
import {
  Card,
  cx,
  EmptyState,
  formatTimestamp,
  Pager,
  pagerWaiting,
  StatusPill,
  useClampedPage,
  useScrollRegion,
} from '../ui';
import { ListError } from './ListError';
import { useAdminPaging } from './useAdminPaging';
import { listHref, MAIL_STATUS, recipientWords, statusFromParam, STATUS_ORDER } from './presenter';
import { WriteAsDialog } from './WriteAsDialog';
import o from '../ops/Ops.module.css';
import s from '../close/Close.module.css';

function Filters({ active }: { active: MailStatus | null }): ReactElement {
  const items: { id: MailStatus | null; label: string }[] = [
    { id: null, label: 'All' },
    ...STATUS_ORDER.map((id) => ({ id, label: MAIL_STATUS[id].filter })),
  ];
  return (
    <nav aria-label="Show messages" className={o.filters}>
      {items.map((item) => (
        <RouterLink
          key={item.id ?? 'all'}
          to={listHref(item.id)}
          className={o.filter}
          {...(active === item.id && { 'aria-current': 'page' as const })}
        >
          {item.label}
        </RouterLink>
      ))}
    </nav>
  );
}

function MailTable({
  items,
  empty,
}: {
  items: readonly SentMailSummary[];
  /** The designed empty state, in the one row under the header row. */
  empty: ReactElement;
}): ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);
  const region = useScrollRegion(scrollRef, 'Sent mail');
  return (
    <div ref={scrollRef} className={s.tableScroll} {...region}>
      <table className={cx(s.table, s.wide)}>
        <caption className="visually-hidden">Sent mail, newest first</caption>
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Subject</th>
            <th scope="col">To</th>
            <th scope="col">Event</th>
            <th scope="col">Award</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr className={s.emptyRow}>
              <td colSpan={6}>{empty}</td>
            </tr>
          )}
          {items.map((mail) => {
            const to = recipientWords(mail);
            const status = MAIL_STATUS[mail.status];
            return (
              <tr key={mail.id}>
                <td className={s.nowrap}>{formatTimestamp(mail.createdAt)}</td>
                <td>
                  <Link href={`/settings/system/sent-mail/${mail.id}`} className={o.strongLink}>
                    {mail.subject}
                  </Link>
                </td>
                <td>
                  <span className={s.cellStrong}>{to.name}</span>
                  <span className={o.sub}>{to.address}</span>
                </td>
                <td>{mail.eventLabel}</td>
                <td>{mail.award?.displayId ?? 'Not about one award'}</td>
                <td>
                  <StatusPill size="sm" tone={status.tone} label={status.label} />
                  {mail.lastError !== null && <span className={o.sub}>{mail.lastError}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Settings › System administration › Sent mail: every email the app sent or tried to send, newest first. */
export function SentMailPage(): ReactElement {
  const [params] = useSearchParams();
  const status = statusFromParam(params.get('status'));
  const paging = useAdminPaging('sent_mail');
  const { page, size, ready, anchorRef, goTo, changeSize } = paging;
  const list = useQuery({
    ...sentMailQuery(status, { page, size }),
    enabled: ready,
    placeholderData: keepPreviousData,
  });
  const items = list.data?.items ?? [];
  // A page past the end came back as the last one: the address follows.
  useClampedPage(
    { page, ready, setPage: goTo },
    list.isPlaceholderData ? undefined : list.data?.page.number,
  );
  // APP-071: in test environments, a named tester can write to the email assistant as someone.
  const assistant = useQuery(assistantMailStatusQuery).data;
  const [writing, setWriting] = useState(false);

  return (
    <>
      <AdminFrame
        hero={{
          eyebrow: 'Settings · System administration · System Admin',
          title: 'Sent mail',
          sub: 'Every email MyRewards sent or tried to send, newest first, with where it actually went. Opening one is recorded in the audit log.',
          titleTestId: 'sent-mail-title',
          ...(assistant?.available === true && {
            actions: [
              {
                label: 'Write to the assistant as someone',
                icon: 'mail',
                onPress: () => {
                  setWriting(true);
                },
              },
            ],
          }),
        }}
      >
        <div className={o.stack}>
          {(assistant?.reason === 'testing_off' || assistant?.reason === 'not_tester') && (
            <Notice tone="info">
              To test the email assistant here, you can write to it as someone. That needs Preview
              &amp; testing switched on, with you named as a tester, in{' '}
              <Link href="/settings/system/preview" className={o.inlineLink}>
                Settings › System administration › Preview &amp; testing
              </Link>
              .
            </Notice>
          )}
          <Card
            title="Sent mail"
            help={{
              shows:
                'Each email: when, the subject, who it was for and where it went, the event that sent it, and whether it was sent, kept as a copy, skipped or failed.',
              canDo:
                'Filter by status, then open a message to read its text and HTML and download the .eml copy. Opening one is recorded in the audit log.',
            }}
          >
            <Filters active={status} />
            {/* Until the list is here there is nothing to say is empty: it's loading, or it
                failed and the problem says so. */}
            {list.data === undefined ? (
              list.isError ? (
                <ListError
                  what="The email list"
                  error={list.error}
                  onRetry={() => {
                    void list.refetch();
                  }}
                />
              ) : (
                <p className={o.muted}>Reading the email…</p>
              )
            ) : (
              <section ref={anchorRef} tabIndex={-1}>
                <MailTable
                  items={items}
                  empty={
                    status === null ? (
                      <EmptyState
                        variant="first-day"
                        art="letter"
                        size="sm"
                        headline="No email yet"
                        body="Every email the app sends (approvals, letters, reminders, digests) is listed here with where it went."
                      />
                    ) : (
                      // A filter that matches nothing: search art, never caught up.
                      <EmptyState
                        variant="first-day"
                        art="search"
                        size="sm"
                        headline={`No ${MAIL_STATUS[status].filter.toLowerCase()} email`}
                        body="Nothing with this status right now. Show all email to see everything that went out."
                        action={{ label: 'Show all email', href: listHref(null) }}
                      />
                    )
                  }
                />
              </section>
            )}
            {list.data !== undefined && (
              <Pager
                label="Sent mail"
                page={list.data.page}
                {...pagerWaiting(paging, list.isPlaceholderData)}
                onPage={goTo}
                onSize={(size) => {
                  changeSize(size, pageOffset(list.data.page));
                }}
              />
            )}
          </Card>
        </div>
      </AdminFrame>
      {assistant?.available === true && (
        <WriteAsDialog
          isOpen={writing}
          onOpenChange={setWriting}
          replyTo={null}
          initialPerson={null}
          address={assistant.address}
        />
      )}
    </>
  );
}
